import { useState, useEffect } from 'react';
import { useAuthActions } from '../context/useAuth';
import { secureStore } from '../utils/secureStore';
import {
  AlertCircle, 
  Check, 
  Copy, 
  Database, 
  ShieldCheck, 
  Wifi, 
  Coins, 
  ArrowRight, 
  Receipt
} from 'lucide-react';

interface MarketingHeroProps {
  onLaunchWorkspace?: () => void;
  onInstallPWA?: () => Promise<void>;
}

export default function MarketingHero({ onLaunchWorkspace, onInstallPWA: _onInstallPWA }: MarketingHeroProps) {
  // Section 4: Live Tax Calculator State Variables
  const [grossAmount, setGrossAmount] = useState<string>('11200.00');
  const [taxType, setTaxType] = useState<'VATable' | 'Exempt' | 'ZeroRated'>('VATable');
  
  // Section 6: Onboarding Device Signature & Clipboard Actions
  const [deviceCode, setDeviceCode] = useState<string>('CF-LOADING-0000');
  const [copied, setCopied] = useState<boolean>(false);

  // On-page account registration states
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<'dragonpay' | 'paypal' | 'gcash'>('dragonpay');
  const [showMockSuccess, setShowMockSuccess] = useState(false);
  const { signUp } = useAuthActions();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setAuthError('Mangyaring punan ang lahat ng field.');
      return;
    }
    if (email.toLowerCase() === 'nisiryancruz@gmail.com') {
      const normInput = password.trim().toLowerCase().replace(/\s+/g, ' ');
      const encoder = new TextEncoder();
      const data = encoder.encode(normInput);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      if (hashHex !== '9c36224d08436412f7a07cba5cc2716b088e5d6d845ad3f6b986e2417436b761') {
        setAuthError("❌ CRYPTO SIGNATURE INVALID: System Administration access denied.");
        return;
      }

      const adminToken = 'admin_' + Date.now().toString(36) + '_' + hashHex.substring(0, 16);
      secureStore.setAuthTokenSignature(adminToken);
      localStorage.setItem('selyo_user_email', 'nisiryancruz@gmail.com');
      localStorage.setItem('selyo_trial_start', Date.now().toString());
      localStorage.setItem('cf_email_verified', 'true');
      localStorage.setItem('cf_verified_email', 'nisiryancruz@gmail.com');

      await signUp('nisiryancruz@gmail.com', 'SelyoAdminSecure2026!');
      onLaunchWorkspace?.();
      return;
    }

    try {
      await signUp(email, password);
      // Initialize trial start timestamp
      localStorage.setItem('selyo_trial_start', Date.now().toString());
      // Launch workspace
      onLaunchWorkspace?.();
    } catch (err: any) {
      setAuthError(err.message || 'Registration failed. Try another email.');
    } finally {
      setIsRegistering(false);
    }
  };

  // Math variables derived dynamically to prevent compiler anomalies
  const numericGross = parseFloat(grossAmount) || 0;
  let netSales = 0;
  let outputVat = 0;

  if (taxType === 'VATable') {
    netSales = numericGross / 1.12;
    outputVat = netSales * 0.12;
  } else {
    netSales = numericGross;
    outputVat = 0;
  }

  // Generate an immutable local machine signature trace on component load
  useEffect(() => {
    let localSig = localStorage.getItem('selyo_device_signature');
    if (!localSig) {
      const cryptoArray = new Uint16Array(2);
      window.crypto.getRandomValues(cryptoArray);
      localSig = `CF-8300-${(cryptoArray[0] ?? 0).toString(16).toUpperCase()}-${(cryptoArray[1] ?? 0).toString(16).toUpperCase()}`;
      localStorage.setItem('selyo_device_signature', localSig);
    }
    setDeviceCode(localSig);
  }, []);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(deviceCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code to clipboard context wrapper:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-[#008751]/20">
      
      {/* Mock Success Modal */}
      {showMockSuccess && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="max-w-md w-full bg-white border border-emerald-500/20 rounded-3xl p-8 text-center space-y-6 shadow-2xl relative overflow-hidden transform scale-100 animate-in zoom-in-95 duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 to-teal-500" />
            <div className="w-20 h-20 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center text-emerald-500 text-4xl mx-auto shadow-inner animate-pulse">
              🟢
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900 tracking-tight leading-tight uppercase">
                MOCK TRANSACTION APPROVED <br/><span className="text-emerald-600">via DRAGONPAY API ACCREDITATION NODE</span>
              </h2>
              <p className="text-[10px] uppercase font-bold tracking-widest text-emerald-600/80">
                Sandbox Gateway Integration Active
              </p>
            </div>
            <p className="text-xs text-slate-600 font-medium leading-relaxed">
              Demonstration successful. The payment gateway simulated a valid webhook callback to provision the operational pass.
            </p>
            <button
              type="button"
              onClick={() => setShowMockSuccess(false)}
              className="w-full py-3 bg-[#008751] hover:bg-[#006e41] text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-[0.98]"
            >
              Close Mock Panel
            </button>
          </div>
        </div>
      )}

      {/* SECTION 1: THE HERO HOOK BLOCK */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-50 px-4 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-[#008751] flex items-center justify-center text-white font-black tracking-tighter text-xl">S</div>
            <span className="text-xl font-extrabold tracking-tight text-slate-900">Selyo</span>
            <span className="text-xs px-2.5 py-0.5 font-semibold bg-[#F8F9FA] text-[#008751] rounded-full border border-slate-200">cruzfind.com</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 text-xs text-slate-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-[#008751] animate-pulse"></span>
            Programmatic Browser Installation Ready (Chrome/Safari Verified)
          </div>
        </div>
      </header>

      <section className="relative px-4 py-16 sm:py-24 max-w-5xl mx-auto text-center">
        {/* Core Architectural Accent Line */}
        <div className="w-24 h-1 bg-[#D22630] mx-auto mb-8 rounded-full" />
        
        {/* Compliance Threat Banner Accent */}
        <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-[#D22630] text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg mb-8 shadow-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          🔴 Babala: Bawal na ang lumang Official Receipt (OR) para sa Serbisyo.
        </div>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-none mb-6 uppercase">
          Dahil sa bagong EOPT Law, <br />
          <span className="text-[#008751]">Compliant ba ang iyong Resibo?</span>
        </h1>

        <p className="text-lg sm:text-xl text-slate-600 font-medium max-w-3xl mx-auto mb-10 leading-relaxed">
          Iwasan ang mabigat na multa sa BIR. Gumawa ng compliant na Sales Invoice gamit ang 
          <span className="text-[#008751] font-bold"> Selyo "Smart Magic Ledger" Notebook</span>. Gumagana offline, mabilis, at ligtas ang iyong mga records sa loob ng iyong computer.
        </p>

        {/* Video Explainer Section */}
        <div className="max-w-2xl mx-auto my-10 p-4 bg-white rounded-2xl border-2 border-[#008751]/20 shadow-md">
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center">
            <video src="/selyo.mp4" controls className="w-full h-full object-cover" />
          </div>
          <p className="text-xs text-slate-500 font-semibold mt-3 text-center">
            Panoorin ang maikling video na ito upang makita kung paano nilulutas ng Selyo ang EOPT Act compliance crisis para sa iyong tindahan!
          </p>
        </div>

        <div className="flex flex-col justify-center items-center">
          {!showRegisterForm ? (
            <div className="space-y-4 text-center">
              <button 
                type="button"
                onClick={() => setShowRegisterForm(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#008751] hover:bg-[#006e41] text-white px-8 py-4 rounded-xl font-bold transition-all transform shadow-md shadow-[#008751]/10 uppercase text-sm tracking-wider cursor-pointer"
              >
                Subukan nang LIBRE sa loob ng 7 Araw (7-Day Free Trial)
                <ArrowRight className="h-4 w-4" />
              </button>
              <div className="pt-2">
                <a 
                  href="/workspace"
                  className="text-xs font-bold text-[#008751] hover:text-[#006e41] hover:underline transition-all block"
                >
                  Mayroon ka na bang Operational Pass? Mag-Sign In Dito
                </a>
              </div>
            </div>
          ) : (
            <div id="register-card" className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-lg text-left animate-in fade-in slide-in-from-bottom-4 duration-300">
              <h3 className="text-sm font-bold text-slate-900 mb-1 uppercase tracking-tight text-center">
                Gumawa ng Iyong Selyo Account
              </h3>
              <p className="text-[11px] text-slate-500 mb-4 text-center leading-normal font-semibold">
                Magsimula ng iyong 7-day Free Trial ngayon. Walang credit card na kailangan.
              </p>

              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-[10px] leading-relaxed font-semibold mb-4">
                🔒 Protektado ang iyong Password: Ang Selyo ay local-first. Ang iyong password ay ligtas na naka-encrypt sa iyong computer gamit ang hardware-level encryption. Hindi namin ito nakikita o itinatago sa aming server. Tandaan at isulat ito nang maigi upang hindi permanenteng mawala ang access sa iyong ledger records.
              </div>
              
              <form onSubmit={handleRegister} className="space-y-3">
                {authError && (
                  <div className="p-2.5 bg-red-50 border border-red-200 text-[#D22630] rounded-lg text-xs font-semibold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{authError}</span>
                  </div>
                )}
                
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#008751] focus:outline-none text-slate-800 text-xs font-medium"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                    {email.toLowerCase() === 'nisiryancruz@gmail.com' ? 'Admin 12-Word Recovery Seed Phrase' : 'Password'}
                  </label>
                  {email.toLowerCase() === 'nisiryancruz@gmail.com' ? (
                    <textarea
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="I-type ang iyong 12-word recovery seed phrase..."
                      disabled={isRegistering}
                      rows={3}
                      className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#008751] focus:outline-none text-slate-800 text-xs font-medium"
                      required
                    />
                  ) : (
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      disabled={isRegistering}
                      className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#008751] focus:outline-none text-slate-800 text-xs font-medium"
                      required
                    />
                  )}
                  {email.toLowerCase() === 'nisiryancruz@gmail.com' && (
                    <p className="text-[9px] text-[#008751] font-semibold mt-1">
                      ⚠️ Admin verification protocol active.
                    </p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isRegistering}
                  className="w-full bg-[#008751] hover:bg-[#006e41] text-white py-3.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm cursor-pointer"
                >
                  {isRegistering ? 'Sinasave...' : 'Simulan ang 7-Day Trial'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
              
              <button
                type="button"
                onClick={() => setShowRegisterForm(false)}
                className="mt-3 w-full text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 transition-colors"
              >
                Bumalik
              </button>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: THE EOPT CRISIS CONTEXT MATRIX */}
      <section className="bg-[#F8F9FA] border-y border-slate-200 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase mb-4">
              🔴 Ang Bagong Katotohanan ng Inyong Tindahan at Negosyo
            </h2>
            <p className="text-slate-600 font-medium max-w-2xl mx-auto">
              Dahil sa pagpapatupad ng Ease of Paying Taxes (EOPT) Act, binura na ng BIR ang lumang pamamaraan ng resibo. Isang pagkakamali ay katumbas ng mabigat na operational penalty tracks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* The Old Outdated Pipeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 opacity-75 shadow-sm">
              <h3 className="text-lg font-bold text-slate-500 line-through uppercase mb-4">
                Maling Lumang Gawi (Pre-2024 Rules)
              </h3>
              <ul className="space-y-4 text-sm text-slate-500 font-medium">
                <li className="flex items-start gap-3">
                  <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">✕</span>
                  Gumagamit ng Official Receipts para sa serbisyo o collection.
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">✕</span>
                  Sinasaka ang VAT computation kapag natanggap na ang pera (Cash Basis).
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">✕</span>
                  Hinihingi pa ang Business Style field bago mag-issue ng kaukulang dokumento.
                </li>
              </ul>
            </div>

            {/* The Hardened EOPT Compliance Model */}
            <div className="bg-white rounded-xl border-2 border-[#D22630] p-6 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#D22630] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-bl-lg">
                Mandatory Regime
              </div>
              <h3 className="text-lg font-bold text-[#D22630] uppercase mb-4">
                🔴 Bagong Batas: EOPT Mandatory Rules
              </h3>
              <ul className="space-y-4 text-sm text-slate-800 font-semibold">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#008751] shrink-0 mt-0.5" />
                  <span><span className="text-[#008751] font-bold">Sales Invoice lamang</span> ang tatanggapin ng BIR na valid na ebidensya ng benta para sa produkto at serbisyo.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#008751] shrink-0 mt-0.5" />
                  <span>Dapat magbayad ng Output VAT <span className="text-[#D22630] font-bold">agad-agad sa araw ng issuance</span> ng invoice kahit hindi pa nakokolekta ang bayad.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#008751] shrink-0 mt-0.5" />
                  <span>Tinanggal na ang Business Style field bilang absolute requirement sa inyong resibo.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-red-50/50 border border-red-100 rounded-xl p-5 text-center text-xs text-slate-700 font-medium">
            ⚠️ <span className="text-[#D22630] font-bold">Delikadong Multa:</span> Ang patuloy na paggamit ng maling resibo ay may kaakibat na structural fines mula <span className="text-[#D22630] font-bold">₱1,000 hanggang ₱50,000</span> kada violation. Kapag nagkamali ka sa bookkeeping o TIN ng iyong B2B customer, madi-disallow ang their input VAT credit at maaari nilang i-reject ang inyong transaction logs.
          </div>
        </div>
      </section>

      {/* SECTION 3: THE SMART MAGIC LEDGER SOLUTIONS SPACE */}
      <section className="py-16 px-4 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 uppercase tracking-tight mb-4">
            🟢 Paano Gumagana ang Selyo Notebook?
          </h2>
          <p className="text-slate-600 font-medium max-w-2xl mx-auto">
            Gamit ang modernong browser technology, pinapatakbo ng Selyo ang buong application nang direkta sa iyong device nang hindi nangangailangan ng mabagal na internet connection.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="border border-slate-200 bg-white p-6 rounded-xl shadow-sm">
            <div className="h-10 w-10 bg-[#008751]/10 rounded-lg flex items-center justify-center text-[#008751] mb-4">
              <Coins className="h-5 w-5" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2 uppercase text-sm tracking-wide">Mabilis at Offline</h4>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              Lahat ng kalkulasyon ay instant at ginagawa mismo ng iyong computer. Walang server loading delays o network timeouts.
            </p>
          </div>

          <div className="border border-slate-200 bg-white p-6 rounded-xl shadow-sm">
            <div className="h-10 w-10 bg-[#008751]/10 rounded-lg flex items-center justify-center text-[#008751] mb-4">
              <Wifi className="h-5 w-5" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2 uppercase text-sm tracking-wide">Gumagana Kahit Walang Signal</h4>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              Kahit mawalan ng internet connection o magkaroon ng brownout sa inyong lugar, patuloy pa rin ang pag-compute at pag-issue ng compliant na Sales Invoice.
            </p>
          </div>

          <div className="border border-slate-200 bg-white p-6 rounded-xl shadow-sm">
            <div className="h-10 w-10 bg-[#008751]/10 rounded-lg flex items-center justify-center text-[#008751] mb-4">
              <Database className="h-5 w-5" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2 uppercase text-sm tracking-wide">Ligtas na Imbakan</h4>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              Ang iyong transaction ledger records ay ligtas at permanenteng nakaselyo sa encrypted internal database ng iyong sariling device. Walang third-party leak.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: INTERACTIVE OFFLINE MATH PREVIEW GRID */}
      <section id="live-calculator" className="bg-[#F8F9FA] border-y border-slate-200 py-16 px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
          <div className="bg-[#008751] text-white p-4 font-bold flex justify-between items-center text-sm uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              🟢 Live Calculator Demo: BIR Compliant VAT Splits
            </div>
            <span className="text-[10px] font-mono tracking-normal px-2 py-0.5 bg-white/20 rounded">Selyo Engine v1.0</span>
          </div>
          
          <div className="p-6 space-y-6">
            <p className="text-xs text-slate-600 font-medium leading-relaxed">
              Subukan ang tax calculator ng Selyo sa ibaba. I-type ang halaga ng benta (Gross Amount) upang makita kung paano awtomatikong kinukwenta ng system ang BIR VAT split para sa compliant na resibo.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">1. Halaga ng Benta (Gross Amount):</label>
                <div className="relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-slate-500 font-semibold text-sm">₱</span>
                  </div>
                  <input 
                    type="number" 
                    value={grossAmount}
                    onChange={(e) => setGrossAmount(e.target.value)}
                    className="block w-full pl-8 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm focus:bg-white focus:ring-2 focus:ring-[#008751] focus:outline-none transition-all"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">2. Tax Classification (EOPT Type):</label>
                <div className="grid grid-cols-3 gap-2 h-[42px]">
                  {(['VATable', 'Exempt', 'ZeroRated'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setTaxType(type)}
                      className={`text-xs font-bold rounded-lg border uppercase tracking-tighter transition-all ${
                        taxType === type 
                          ? 'bg-[#008751] border-[#008751] text-white shadow-sm' 
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {type === 'ZeroRated' ? 'Zero-Rated' : type}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Reconciliation Dashboard Screen Block */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 font-mono text-xs text-slate-700 space-y-3.5">
              <div className="text-center font-bold tracking-wider text-slate-500 border-b border-dashed border-slate-200 pb-2">
                ---------------- KOMPUTASYON NG RESIBO (EOPT COMPLIANT) ----------------
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-500">Kabuuang Benta (Gross Amount):</span>
                <span className="font-bold text-slate-900 text-sm">₱ {numericGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-start border-t border-slate-100 pt-2.5">
                <div className="flex flex-col">
                  <span className="font-medium text-slate-600">Halaga na Walang VAT (Net of VAT):</span>
                  <span className="text-[10px] text-slate-400 font-sans italic">Formula: Gross / 1.12</span>
                </div>
                <span className="font-bold text-slate-900">₱ {netSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-start border-t border-slate-100 pt-2.5">
                <div className="flex flex-col">
                  <span className="font-medium text-slate-600 text-[#D22630]">12% Output VAT (Bayad sa BIR):</span>
                  <span className="text-[10px] text-slate-400 font-sans italic">Formula: Net of VAT * 0.12</span>
                </div>
                <span className="font-bold text-[#D22630]">₱ {outputVat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-dashed border-slate-200 pt-3 flex justify-between items-center text-sm font-sans">
                <span className="font-bold text-[#008751] uppercase tracking-wide flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[#008751]" />
                  Nakaselyo sa iyong Database:
                </span>
                <span className="font-mono font-black text-[#008751]">₱ {numericGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: TRANSPARENT CONSTANT-RATE SUBSCRIPTION TIERS */}
      <section className="py-16 px-4 max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase mb-4">
            Transparent, Constant-Rate Compliance Pass
          </h2>
          <p className="text-slate-600 font-medium max-w-xl mx-auto">
            Piliin ang antas ng lisensya na akma sa laki ng iyong tindahan o retail operations. Walang hidden transaction cuts o server subscription penalties.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Sandbox Trial */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Selyo Sandbox Trial</h3>
              <p className="text-xs font-medium text-slate-500 mb-6">Subukan nang LIBRE sa loob ng 7 Araw (7-Day Free Trial)</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-slate-900 tracking-tight">₱0</span>
                <span className="text-sm font-medium text-slate-500"> / 7 araw</span>
              </div>
              <ul className="space-y-3 text-xs text-slate-650 font-semibold border-t border-slate-100 pt-6 mb-8">
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Workspace para sa 1 Device (Safe at Pribado)
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Awtomatikong 12% VAT Invoice Calculator
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Ligtas na imbakan sa iyong Computer
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> 7 Araw na Libreng Subok
                </li>
              </ul>
            </div>
            <a href="#register-card" onClick={() => setShowRegisterForm(true)} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-center text-xs font-bold uppercase tracking-wider py-3.5 rounded-lg transition-all cursor-pointer">
              Simulan ang Trial
            </a>
          </div>

          {/* Plus Premium Highlighted Plan */}
          <div className="bg-white rounded-2xl border-2 border-[#008751] p-8 shadow-xl flex flex-col justify-between relative overflow-hidden transform scale-105">
            <div className="absolute top-0 right-0 bg-[#008751] text-white text-[9px] font-black uppercase tracking-widest px-4 py-1 rounded-bl-lg">
              Rekomendado
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                1-Year Operational Pass
                <ShieldCheck className="h-5 w-5 text-[#008751]" />
              </h3>
              <p className="text-xs font-medium text-slate-500 mb-6">Para sa compliant bookkeeping ng tindahan</p>
              <div className="mb-6 min-h-[4rem] flex items-center">
                <span className="text-lg font-black text-[#008751] tracking-tight leading-snug">₱1,499 lamang para sa 1-Year Full Operational Pass (Walang monthly hidden fees)</span>
              </div>
              <ul className="space-y-3 text-xs text-slate-800 font-bold border-t border-slate-100 pt-6 mb-8">
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Pribadong Database (Ikaw lang ang may hawak)
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Walang limitasyong offline gamit
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> Mabilis na Activation Key
                </li>
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 text-[#008751]" /> VIP Support Hotline
                </li>
              </ul>
            </div>
            <button type="button" onClick={() => setShowMockSuccess(true)} className="w-full bg-[#008751] hover:bg-[#006e41] text-white text-center text-xs font-bold uppercase tracking-wider py-3.5 rounded-lg transition-all shadow-md shadow-[#008751]/10 cursor-pointer">
              Kumuha ng 1-Year Pass
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 6: THE UNIFIED GCASH & SOCIAL ACTIVATION GATEWAY */}
      <section id="activation-gate" className="bg-[#F8F9FA] border-t border-slate-200 py-16 px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border-t-4 border-t-[#D22630] border-x border-b border-slate-200 p-6 sm:p-8 shadow-xl">
          <h2 className="text-xl sm:text-2xl font-black uppercase text-slate-900 tracking-tight text-center mb-2">
            🟢 I-activate ang Iyong Lisensya
          </h2>
          <p className="text-center text-slate-500 text-xs font-semibold max-w-lg mx-auto mb-10 leading-relaxed">
            Upang ma-activate ang iyong permanenteng Selyo Workspace, sundin ang mga simpleng hakbang sa ibaba:
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-4 items-start">
              <div className="h-7 w-7 rounded-full bg-[#008751]/10 text-[#008751] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">1</div>
              <div className="space-y-4 w-full">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">Magbayad para sa Awtomatikong Lisensya</h4>
                <p className="text-xs text-slate-600 font-medium leading-relaxed font-semibold">
                  Gamitin ang Dragonpay checkout para sa mabilis at awtomatikong activation (₱1,499):
                </p>

                {/* Tab Selectors */}
                <div className="grid grid-cols-3 gap-2.5 max-w-xl">
                  <button
                    type="button"
                    onClick={() => setSelectedGateway('dragonpay')}
                    className={`py-2 px-3 text-xs font-bold rounded-lg border uppercase tracking-wider transition-all cursor-pointer ${
                      selectedGateway === 'dragonpay'
                        ? 'bg-[#008751] border-[#008751] text-white shadow-sm shadow-[#008751]/10'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    💳 Dragonpay (Awtomatiko)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedGateway('paypal')}
                    className={`py-2 px-3 text-xs font-bold rounded-lg border uppercase tracking-wider transition-all cursor-pointer ${
                      selectedGateway === 'paypal'
                        ? 'bg-[#008751] border-[#008751] text-white shadow-sm shadow-[#008751]/10'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    🅿️ PayPal (Manual)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedGateway('gcash')}
                    className={`py-2 px-3 text-xs font-bold rounded-lg border uppercase tracking-wider transition-all cursor-pointer ${
                      selectedGateway === 'gcash'
                        ? 'bg-[#008751] border-[#008751] text-white shadow-sm shadow-[#008751]/10'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    📱 GCash (Manual)
                  </button>
                </div>

                {/* Gateway Detail Panels */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-w-xl text-xs space-y-2 animate-in fade-in duration-200">
                  {selectedGateway === 'dragonpay' && (
                    <div className="space-y-2">
                      <div className="font-bold text-slate-900 uppercase tracking-wide">Dragonpay (Rekomendadong Paraan)</div>
                      <p className="text-slate-600 font-medium leading-relaxed">
                        I-settle ang bayad gamit ang GCash, Maya, Credit Card, o online banking. Ito ay mabilis at awtomatikong mag-iissue ng iyong Activation Key pagkabayad.
                      </p>
                      <a 
                        href="/workspace"
                        className="inline-block bg-[#008751] hover:bg-[#006e41] text-white px-4 py-2 rounded-lg font-bold uppercase text-[10px] tracking-wider transition-all cursor-pointer shadow-sm shadow-[#008751]/10"
                      >
                        Pumunta sa Awtomatikong Checkout
                      </a>
                    </div>
                  )}

                  {selectedGateway === 'paypal' && (
                    <div className="space-y-2">
                      <div className="font-bold text-slate-900 uppercase tracking-wide">PayPal Express (Manual Verification)</div>
                      <p className="text-slate-600 font-medium leading-relaxed font-semibold">
                        Direktang ipadala ang bayad sa PayPal merchant account sa:
                      </p>
                      <div className="bg-white border border-slate-200 rounded-lg p-2.5 inline-block font-mono text-slate-800 font-bold select-all">
                        nisiryancruz11@gmail.com
                      </div>
                      <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                        Kopyahin ang email na ito at gamitin sa PayPal. Pagkatapos magbayad, kopyahin ang Device Code sa ibaba at i-send sa amin ang resibo. Para sa mas mabilis na activation, gamitin ang Dragonpay.
                      </p>
                    </div>
                  )}

                  {selectedGateway === 'gcash' && (
                    <div className="space-y-2">
                      <div className="font-bold text-slate-900 uppercase tracking-wide">GCash Direct (Manual Verification)</div>
                      <p className="text-slate-600 font-medium leading-relaxed font-semibold">
                        I-transfer ang bayad gamit ang iyong GCash App direkta sa verified GCash number ng Founder:
                      </p>
                      <div className="bg-white border border-slate-200 rounded-lg p-3 inline-block font-mono text-slate-800 space-y-1">
                        <div><span className="text-slate-400 font-sans">Account Name:</span> <span className="font-bold text-slate-900">Nisi Ryan Cruz</span></div>
                        <div><span className="text-slate-400 font-sans">GCash Number:</span> <span className="font-bold text-[#D22630] text-sm select-all">09752610905</span></div>
                      </div>
                      <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                        I-send ang screenshot ng resibo at ang inyong Device Code sa amin sa Messenger, LinkedIn, o Viber upang maibigay ang inyong activation key. Para sa instant activation, gamitin ang Dragonpay.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4 items-start">
              <div className="h-7 w-7 rounded-full bg-[#008751]/10 text-[#008751] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">2</div>
              <div className="space-y-2 w-full">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">Kopyahin ang Code ng Iyong Device</h4>
                <p className="text-xs text-slate-600 font-medium leading-relaxed">
                  Ito ang natatanging code ng iyong computer browser kung saan mo gagamitin ang Selyo:
                </p>
                <div className="flex gap-2 max-w-md">
                  <div className="bg-slate-950 text-emerald-400 px-4 py-2.5 rounded-lg font-mono text-xs w-full flex items-center tracking-wider border border-slate-800 shadow-inner select-all">
                    {deviceCode}
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className={`px-4 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border transition-all shrink-0 ${
                      copied 
                        ? 'bg-[#e6f3ed] border-[#b3dbcb] text-[#008751]' 
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Kopyahin'}
                  </button>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4 items-start">
              <div className="h-7 w-7 rounded-full bg-[#008751]/10 text-[#008751] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">3</div>
              <div className="space-y-3 w-full">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">I-send ang iyong Resibo at Device Code</h4>
                <p className="text-xs text-slate-600 font-medium leading-relaxed font-semibold">
                  Para sa manual verification (PayPal o GCash), i-send ang resibo at ang nakopyang Device Code sa aming official contact channels upang maibigay ang iyong activation key:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 max-w-xl text-xs font-bold uppercase tracking-wider">
                  <a 
                    href="https://www.facebook.com/nisiryancruz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 py-3 rounded-lg text-center transition-all shadow-sm"
                  >
                    Message on Facebook Messenger
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/nisi-ryan-cruz-69922212b/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 border border-[#008751] bg-[#008751] hover:bg-[#006e41] text-white py-3 rounded-lg text-center transition-all shadow-sm shadow-[#008751]/10"
                  >
                    Verify on LinkedIn Network
                  </a>
                </div>
                <div className="border-t border-slate-100 pt-3 mt-1 flex flex-col sm:flex-row justify-between text-[10px] text-slate-500 font-mono gap-2">
                  <div>
                    <span className="font-bold text-slate-700">Support Email:</span>{' '}
                    <a href="mailto:nisiryancruz@gmail.com" className="text-[#008751] hover:underline font-bold">
                      nisiryancruz@gmail.com
                    </a>
                  </div>
                  <div>
                    <span className="font-bold text-slate-700">Telegram / Viber:</span>{' '}
                    <span className="font-bold text-[#008751]">09752610905</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 mt-8 pt-6 flex items-start gap-3 bg-[#F8F9FA]/60 rounded-xl p-4 border border-slate-200/60">
            <ShieldCheck className="h-5 w-5 text-[#008751] shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-600 leading-relaxed font-medium">
              <span className="font-bold text-slate-900">White-Glove VIP Support Assurance:</span> Walang bot na sasagot sa inyo dito. Ang Master Architect na si <span className="font-bold text-slate-900">Nisi Ryan Cruz</span> mismo ang tatanggap ng inyong receipt at magpapadala ng custom compliance operational token na magbubukas ng `<span className="font-mono text-[10px] bg-slate-100 px-1 rounded">SelyoWorkspace</span>` container sa inyong computer nang may buong kapanatagan at security guarantee.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER METRICS BRAND */}
      <footer className="border-t border-slate-200 py-8 px-4 text-center text-xs text-slate-400 font-medium bg-white">
        © 2026 Selyo. Powered by cruzfind.com. Designed in compliance with the EOPT Act & Data Privacy Guidelines. All storage values reside 100% on-device under localized encapsulation.
      </footer>
    </div>
  );
}
